import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Usuario, LoginRequest, Organization } from '../types';
import { apiService } from '../services/api';
import { wsService } from '../services/websocket';

interface AuthContextType {
  usuario: Usuario | null;
  organization: Organization | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshOrganization: () => Promise<void>;
  refreshUsuario: () => Promise<void>;
  isOwner: boolean;
  isAdmin: boolean;
  isGerente: boolean;
  isSuperAdmin: boolean;
  canViewBacklog: boolean;
  isAuthenticated: boolean;
  organizationId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOrganization = useCallback(async (orgId: string) => {
    try {
      const orgData = await apiService.getOrganization(orgId);
      setOrganization(orgData);
      localStorage.setItem('organization', JSON.stringify(orgData));
    } catch (error) {
      console.error('Erro ao carregar organização:', error);
      // Use cached data if available
      const storedOrganization = localStorage.getItem('organization');
      if (storedOrganization) {
        setOrganization(JSON.parse(storedOrganization));
      }
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUsuario = localStorage.getItem('usuario');
      
      if (token && storedUsuario) {
        try {
          const meResponse = await apiService.getMe();
          // getMe returns { user, organization } - extract user
          const usuarioData = (meResponse as any).user || meResponse;
          setUsuario(usuarioData);
          localStorage.setItem('usuario', JSON.stringify(usuarioData));
          
          // Also update organization from getMe response
          if ((meResponse as any).organization) {
            setOrganization((meResponse as any).organization);
            localStorage.setItem('organization', JSON.stringify((meResponse as any).organization));
          }
          
          // Load organization if user has one
          if (usuarioData.organizationId) {
            await loadOrganization(usuarioData.organizationId);
          }

          // Connect WebSocket for real-time updates
          wsService.connect(token);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('usuario');
          localStorage.removeItem('organization');
        }
      }
      setLoading(false);
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      wsService.disconnect();
    };
  }, [loadOrganization]);

  const login = async (credentials: LoginRequest) => {
    const response = await apiService.login(credentials);
    localStorage.setItem('token', response.token);
    localStorage.setItem('usuario', JSON.stringify(response.user));
    setUsuario(response.user);

    // Set organization from response or fetch it
    if (response.organization) {
      setOrganization(response.organization);
      localStorage.setItem('organization', JSON.stringify(response.organization));
    } else if (response.user.organizationId) {
      await loadOrganization(response.user.organizationId);
    }

    // Connect WebSocket for real-time updates
    wsService.connect(response.token);
  };

  const logout = async () => {
    // Disconnect WebSocket
    wsService.disconnect();

    try {
      await apiService.logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      localStorage.removeItem('organization');
      setUsuario(null);
      setOrganization(null);
    }
  };

  const refreshOrganization = useCallback(async () => {
    if (usuario?.organizationId) {
      await loadOrganization(usuario.organizationId);
    }
  }, [usuario?.organizationId, loadOrganization]);

  const refreshUsuario = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const meResponse = await apiService.getMe();
      const usuarioData = (meResponse as any).user || meResponse;
      setUsuario(usuarioData);
      localStorage.setItem('usuario', JSON.stringify(usuarioData));
      if ((meResponse as any).organization) {
        setOrganization((meResponse as any).organization);
        localStorage.setItem('organization', JSON.stringify((meResponse as any).organization));
      }
    } catch (e) {
      console.error('Erro ao atualizar usuário:', e);
    }
  }, []);

  // Normalize role to make permission checks resilient to format variations.
  const normalizedRole = String(usuario?.role || '').trim().toLowerCase();

  // Calculate permission flags
  const isSuperAdmin = ['super_admin', 'super-admin', 'superadmin'].includes(normalizedRole);
  const isOwner = normalizedRole === 'owner';
  const isAdmin = ['admin', 'owner'].includes(normalizedRole);
  const isGerente = normalizedRole === 'gerente' || isAdmin;
  
  // canViewBacklog: owner, admin, gerente sempre podem; atendente apenas com flag
  const canViewBacklog = usuario
    ? ['owner', 'admin', 'gerente'].includes(normalizedRole) || usuario.podeVerBacklog === true
    : false;

  const value: AuthContextType = {
    usuario,
    organization,
    loading,
    login,
    logout,
    refreshOrganization,
    refreshUsuario,
    isOwner,
    isAdmin,
    isGerente,
    isSuperAdmin,
    canViewBacklog,
    isAuthenticated: !!usuario,
    organizationId: usuario?.organizationId || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
